<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useContractsStore } from '@/stores/contract.js';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useServerTable } from '@/composables/useServerTable.js';
import { fancyDateShort} from "@/filters/fancyDate.js";


const props = defineProps({
  search: { type: String, default: '' },
  // как у customers: true = активные, false = архив
  isActive: { type: Boolean, required: false, default: undefined }
});

const emit = defineEmits(['initial-count-loaded']);
const initialLoadEmitted = ref(false);


const contractsStore = useContractsStore();
const router = useRouter();

const {
  data: contracts,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  sortField,
  sortOrder,
  setParams,
  load,
  setSearch,
  setExtra,
  onPageChange,
  onSort,
  firstIndex
} = useServerTable({
  initialRows: 10,
  debounceMs: 800,
  storageKey: 'table.contracts.pageSize',
  loadPage: async ({ page, rows, search, ...extra }) => {
    return await contractsStore.loadContractsList({
      page,
      page_size: rows,
      search,
      is_active: typeof extra.is_active === 'boolean' ? extra.is_active : undefined,
      ordering: extra.ordering
    });
  }
});

const goToContract = async (event) => {
  if (event.data && event.data.id) {
    await router.push(`/contracts/${event.data.id}`);
  }
};

watch(
    () => [props.search, props.isActive],
    (newVals, oldVals) => {
      const isInitialLoad = oldVals === undefined;
      const [search, isActive] = newVals || [];

      if (!isInitialLoad) {
        const [prevSearch, prevIsActive] = oldVals || [];
        if (search === prevSearch && isActive === prevIsActive) {
          return;
        }
      }

      setParams({
        search: (search || '').trim(),
        extra: { is_active: typeof isActive === 'boolean' ? isActive : undefined },
        resetPage: true,
        immediate: isInitialLoad,
      });
    },
    { immediate: true }
);

// Эмитим count после первой загрузки
watch(totalRecords, (count) => {
  if (!initialLoadEmitted.value && count !== null) {
    initialLoadEmitted.value = true;
    emit('initial-count-loaded', { count, isActive: props.isActive });
  }
});

</script>


<template>
  <DataTable :value="contracts"
             stripedRows
             lazy
             paginator
             scrollable
             scrollHeight="flex"
             :rows="rowsPerPage"
             :rowsPerPageOptions="[10, 20, 30]"
             :totalRecords="totalRecords"
             :loading="loading"
             :first="firstIndex()"
             :sortField="sortField"
             :sortOrder="sortOrder"
             @page="onPageChange"
             @sort="onSort"
             class="cursor-pointer text-sm h-full"
             @row-click="goToContract"
  >
    <Column
        field="number"
        header="Договор"
        sortable
        :headerClass="'w-1/2 lg:w-[25%]'"
        :bodyClass="'w-1/2 lg:w-[25%]'"
    >
      <template #body="{ data }">
        {{ `Договор № ${data.number ?? data.item_number} от ${fancyDateShort(data.date)}` }}
      </template>
    </Column>
    <Column
        field="customer_short_name"
        header="Заказчик"
        sortable
        :headerClass="'w-1/2 lg:w-[25%]'"
        :bodyClass="'w-1/2 lg:w-[25%]'"
    >
      <template #body="{ data }">
        {{ data.customer_short_name ?? data?.orgs?.item_short }}
      </template>
    </Column>
    <Column
        field="facility_short_name"
        header="Объект"
        sortable
        style="width: 40%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        {{ data.facility_short_name ?? data?.objects?.item_short }}
      </template>
    </Column>
    <Column
        field="date"
        header="Дата"
        sortable
        style="width: 10%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        {{ fancyDateShort(data.date) }}
      </template>
    </Column>

  </DataTable>
</template>
